import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ActionProductModal = ({
  isOpen,
  onClose,
  product,
  actionProduct,
  handleChange,
  handleUpdateProduct,
  handleDeleteProduct,
  setCode,
  productCode,
}) => {
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState({
    hour: "12",
    minute: "00",
    period: "AM",
  });

  useEffect(() => {
    if (actionProduct?.startDate) {
      const startDateObj = new Date(actionProduct.startDate);
      setStartDate(startDateObj);

      const hours = startDateObj.getHours();
      const minutes = startDateObj.getMinutes();

      setStartTime({
        hour: hours % 12 === 0 ? "12" : String(hours % 12),
        minute: String(minutes).padStart(2, "0"),
        period: hours >= 12 ? "PM" : "AM",
      });
    }
  }, [actionProduct]);

  // Function to combine date and time
  const combineDateAndTime = (date, time) => {
    const newDate = new Date(date);
    let hours = parseInt(time.hour, 10);
    const minutes = parseInt(time.minute, 10);

    // Convert to 24-hour format
    if (time.period === "PM" && hours !== 12) hours += 12;
    if (time.period === "AM" && hours === 12) hours = 0;

    newDate.setHours(hours, minutes, 0, 0);
    return newDate;
  };

  const handleDateAndTimeChange = () => {
    const combinedDate = combineDateAndTime(startDate, startTime);
    setStartDate(combinedDate);
    handleChange({ target: { name: "startDate", value: combinedDate } });
  };

  const [isDateSet, setIsDateSet] = useState(false);

  const handleCombinedUpdate = () => {
    handleDateAndTimeChange(); // First, update date & time
    setIsDateSet(true); // Mark as updated
  };

  // Trigger `handleUpdateProduct` **only after** `handleDateAndTimeChange` is completed
  useEffect(() => {
    if (isDateSet) {
      setTimeout(() => {
        handleUpdateProduct();
        setIsDateSet(false); // Reset after update
      }, 100);
    }
  }, [isDateSet]);

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center font-roboto">
      <div className="bg-gray-100 rounded-2xl shadow-lg w-[450px] overflow-hidden ">
        {/* Header */}

        <div className="flex flex-row justify-between items-center px-6 py-4 bg-gradient-to-r bg-blue-600">
          <h2 className="text-xl font-semibold text-white">Add Action</h2>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-9 h-9 rounded-full flex justify-center items-center transition hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 ">
          {product.deliveryType ? (
            <div>
              <label className="block text-gray-900 text-lg font-medium">
                Title:
              </label>
              <input
                type="text"
                name="title"
                value={actionProduct.title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <div className="mt-5">
                <label className="block text-gray-900 text-lg font-medium">
                  Tax (%):
                </label>
                <input
                  type="number"
                  name="tax"
                  value={actionProduct.tax ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty input for smooth deletion
                    if (value === "") {
                      handleChange(e); // Handle empty input scenario if needed
                      return;
                    }

                    // Parse to float and check for non-negative value
                    const parsedValue = parseFloat(value);
                    if (!isNaN(parsedValue) && parsedValue >= 0) {
                      handleChange(e);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="flex gap-4 mt-5">
                <div className="w-1/2">
                  <label className="block text-gray-900 text-lg font-medium">
                    Discount Type:
                  </label>
                  <select
                    name="discountType"
                    value={actionProduct.discountType}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="%">%</option>
                    <option value="$">$</option>
                  </select>
                </div>
                <div className="w-1/2">
                  <label className="block text-gray-900 text-lg font-medium">
                    Discount:
                  </label>
                  <input
                    type="number"
                    name="discount"
                    value={actionProduct.discount ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow empty input for smooth deletion
                      if (value === "") {
                        handleChange(e); // Handle empty input scenario if needed
                        return;
                      }

                      // Parse to float and check for non-negative value
                      const parsedValue = parseFloat(value);
                      if (!isNaN(parsedValue) && parsedValue >= 0) {
                        handleChange(e);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
              {/* Start Date and Time */}
              <div className="mt-2">
                <label className="block font-roboto text-md text-gray-900 mb-2">
                  Start Date
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  placeholderText="Select Start Date"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                />
                <div className="flex space-x-2 mt-2">
                  <select
                    value={startTime.hour}
                    onChange={(e) =>
                      setStartTime({ ...startTime, hour: e.target.value })
                    }
                    className="border border-gray-300 p-2 rounded-lg"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <select
                    value={startTime.minute}
                    onChange={(e) =>
                      setStartTime({ ...startTime, minute: e.target.value })
                    }
                    className="border border-gray-300 p-2 rounded-lg"
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i < 10 ? `0${i}` : i}>
                        {i < 10 ? `0${i}` : i}
                      </option>
                    ))}
                  </select>
                  <select
                    value={startTime.period}
                    onChange={(e) =>
                      setStartTime({ ...startTime, period: e.target.value })
                    }
                    className="border border-gray-300 p-2 rounded-lg"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                {/* <button
              onClick={handleDateAndTimeChange}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg mt-3"
            >
              Set Date and Time
            </button> */}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-gray-900 text-lg font-medium">
                Quantity:
              </label>
              <input
                type="number"
                name="quantity"
                value={actionProduct.quantity ?? ""}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow empty input for smooth deletion
                  if (value === "") {
                    handleChange(e); // Handle empty input scenario if needed
                    return;
                  }

                  // Parse to integer and check for non-negative value
                  const parsedValue = parseInt(value, 10);
                  if (!isNaN(parsedValue) && parsedValue >= 0) {
                    handleChange(e);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />

              <div className="mt-5">
                <label className="block text-gray-900 text-lg font-medium">
                  Tax (%):
                </label>
                <input
                  type="number"
                  name="tax"
                  value={actionProduct.tax ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty input for smooth deletion
                    if (value === "") {
                      handleChange(e); // Handle empty input scenario if needed
                      return;
                    }

                    // Parse to float and check for non-negative value
                    const parsedValue = parseFloat(value);
                    if (!isNaN(parsedValue) && parsedValue >= 0) {
                      handleChange(e);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="flex gap-4 mt-5">
                <div className="w-1/2">
                  <label className="block text-gray-900 text-lg font-medium">
                    Discount Type:
                  </label>
                  <select
                    name="discountType"
                    value={actionProduct.discountType}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="%">%</option>
                    <option value="$">$</option>
                  </select>
                </div>
                <div className="w-1/2">
                  <label className="block text-gray-900 text-lg font-medium">
                    Discount:
                  </label>
                  <input
                    type="number"
                    name="discount"
                    value={actionProduct.discount ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow empty input for smooth deletion
                      if (value === "") {
                        handleChange(e); // Handle empty input scenario if needed
                        return;
                      }

                      // Parse to float and check for non-negative value
                      const parsedValue = parseFloat(value);
                      if (!isNaN(parsedValue) && parsedValue >= 0) {
                        handleChange(e);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 flex justify-between bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleCombinedUpdate}
            className="w-32 py-3 font-roboto  bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"
          >
            Save
          </button>
          <button
            onClick={() => {
              handleDeleteProduct(setCode, product.productId);
              onClose(); // Explicitly close modal
            }}
            className="font-robot text-sm text-red-500 hover:border-b hover:border-b-red-500 hover:duration-300"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionProductModal;
